import { useState } from 'react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Check, Loader2, ScanFace, Sparkles, UserCircle } from 'lucide-react';

// Speaker ↔ face mapping for the approved take, so multi-speaker dubs move the right mouths.
export function SceneFaces({ projectId, scene, characters, onScene }) {
  const [busy, setBusy] = useState(null);
  const faceMap = scene.face_map && scene.face_map.video_path === scene.video_path ? scene.face_map : null;
  const [mapping, setMapping] = useState(faceMap?.mapping || {});
  const speakerIds = [...new Set((scene.dialogue_lines || []).filter(l => (l.text || '').trim()).map(l => l.character_id).filter(Boolean))];
  const speakers = speakerIds.map(id => characters.find(c => c.id === id)).filter(Boolean);
  if (speakers.length < 2) return null;
  const stale = scene.face_map && scene.face_map.video_path !== scene.video_path;

  const detect = async () => {
    setBusy('detect');
    try {
      const r = await api.post(`/projects/${projectId}/scenes/${scene.id}/faces/identify`, {}, { timeout: 180000 });
      setMapping(r.data.mapping || {});
      onScene({ ...scene, face_map: r.data });
      toast.success(`${r.data.faces.length} face${r.data.faces.length === 1 ? '' : 's'} detected${Object.keys(r.data.mapping || {}).length ? ' — AI proposed who is who' : ''}`);
    } catch (err) { const d = err.response?.data?.detail || 'Face detection failed'; toast.error(d.includes('|') ? d.split('|')[1] : d); }
    finally { setBusy(null); }
  };
  const save = async () => {
    setBusy('save');
    try {
      const r = await api.put(`/projects/${projectId}/scenes/${scene.id}/faces/mapping`, { mapping });
      onScene({ ...scene, face_map: r.data });
      toast.success('Speaker mapping saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save mapping'); }
    finally { setBusy(null); }
  };
  const unmapped = speakers.filter(sp => !Object.values(mapping).includes(sp.id));
  const dirty = JSON.stringify(mapping) !== JSON.stringify(faceMap?.mapping || {});

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141418] p-4" data-testid="scene-faces">
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <ScanFace className="w-4 h-4 text-[#A78BFA]" />
        <p className="text-sm font-semibold text-white">Speakers &amp; faces</p>
        <span className="text-[10px] text-[#8A8A93]">{speakers.length} speakers in this scene — map each to a face so dubs sync the right mouth.</span>
        <button data-testid="faces-detect" onClick={detect} disabled={!!busy} className="ml-auto ff-btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-50">
          {busy === 'detect' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {faceMap ? 'Re-detect faces' : 'Detect faces'}
        </button>
      </div>
      {stale && <p className="text-[10px] text-[#F59E0B] mb-2">The take changed since faces were detected — detect again.</p>}
      {faceMap ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
            {faceMap.faces.map(f => (
              <div key={f.index} className="rounded-lg bg-black/30 border border-white/[0.06] p-2 flex items-center gap-2" data-testid={`face-card-${f.index}`}>
                <div className="w-12 h-12 rounded-md overflow-hidden bg-[#0a0a0e] border border-white/[0.08] shrink-0 flex items-center justify-center">
                  {f.thumb_path ? <img src={getImageUrl(f.thumb_path)} alt="" className="w-full h-full object-cover" /> : <UserCircle className="w-6 h-6 text-[#55555C]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-[#8A8A93]">Face #{f.index + 1} <span className="text-[#55555C]">· {Math.round((f.start_time || 0) / 1000)}–{Math.round((f.end_time || 0) / 1000)}s</span></p>
                  <select data-testid={`face-select-${f.index}`} value={mapping[String(f.index)] || ''} onChange={e => setMapping(prev => { const n = { ...prev }; if (e.target.value) n[String(f.index)] = e.target.value; else delete n[String(f.index)]; return n; })}
                    className="ff-input text-xs py-1 w-full mt-0.5">
                    <option value="">— not a speaker —</option>
                    {speakers.map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {unmapped.length > 0 ? <p className="text-[10px] text-[#F59E0B]" data-testid="faces-unmapped">Still unassigned: {unmapped.map(s => s.name).join(', ')}</p>
              : <p className="text-[10px] text-[#10B981] flex items-center gap-1"><Check className="w-3 h-3" /> Every speaker has a face</p>}
            {dirty && <button data-testid="faces-save" onClick={save} disabled={!!busy} className="ml-auto ff-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50">{busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save mapping</button>}
          </div>
        </>
      ) : <p className="text-[10px] text-[#55555C]">Detect faces in the approved take; the AI compares them with your character references and proposes the mapping.</p>}
    </div>
  );
}
